import React, { createContext, useContext, useState, ReactNode, useCallback, useMemo } from 'react';

import { Notification } from '@bringg/react-components';
import { PrivilegeTypes, UserType as UserTypeInterface } from '@bringg/types';
import { UserTypeStore } from '@bringg-frontend/global-stores';
import { useTranslation } from 'react-i18next';
import { omit } from 'lodash';
import { useHasFeatureFlag } from '@bringg-frontend/hooks';

import { useHasAccess } from 'bringg-web/utils/privileges';
import UserType, {
	ROUTE_COST_CALCULATION_TYPE_KEY,
	ROUTE_COST_CALCULATION_CONFIGURATION_KEY,
	ADDITIONAL_COSTS_KEYS
} from '../../stores/user-type/domain-objects/user-type';
import { useTranslations } from './use-translations';

interface ModalConfig {
	isOpen: boolean;
	isEditMode: boolean;
	translations: ReturnType<typeof useTranslations>;
	initialValues: Partial<UserTypeInterface>;
	onSubmit: (data: Record<string, unknown>) => Promise<void>;
	onClose: () => void;
}

interface UserTypeModalContextType {
	modalConfig: ModalConfig;
	openCreateModal: () => void;
	openEditModal: (userTypeId: number) => void;
}

const UserTypeModalContext = createContext<UserTypeModalContextType | undefined>(undefined);

export const useUserTypeModal = () => {
	const context = useContext(UserTypeModalContext);
	if (!context) {
		throw new Error('useUserTypeModal must be used within UserTypeModalProvider');
	}
	return context;
};

enum ModalMode {
	CREATE = 'create',
	EDIT = 'edit'
}

interface Props {
	children: ReactNode;
	userTypeStore: UserTypeStore;
}

export const UserTypeModalProvider = ({ children, userTypeStore }: Props) => {
	const { t } = useTranslation();
	const [mode, setMode] = useState<ModalMode | null>(null);
	const [selectedUserType, setSelectedUserType] = useState<Partial<UserTypeInterface> | null>(null);
	const routeCostCalculationEnabled = useHasFeatureFlag('enable_route_cost_calculation');
	const editRouteCostCalculationAllowed = useHasAccess(PrivilegeTypes.ALLOW_DISPATCHER_EDIT_ROUTE_COST_CALCULATION);
	const editAdditionalCostsAllowed = useHasAccess(
		'allow_dispatcher_edit_additional_driver_cost_fields' as PrivilegeTypes
	);
	const isEditMode = mode === ModalMode.EDIT;
	const translations = useTranslations(isEditMode);

	const openCreateModal = useCallback(() => {
		setMode(ModalMode.CREATE);
		setSelectedUserType(null);
	}, []);

	const openEditModal = useCallback(
		(userTypeId: number) => {
			const userType = userTypeStore?.get(userTypeId);
			if (userType) {
				setSelectedUserType(userType.asJson as Partial<UserTypeInterface>);
				setMode(ModalMode.EDIT);
			}
		},
		[userTypeStore]
	);

	const closeModal = useCallback(() => {
		setSelectedUserType(null);
		setMode(null);
	}, []);

	const prepareFormData = useCallback(
		(formData: Partial<UserTypeInterface>): Partial<UserTypeInterface> => {
			if (!routeCostCalculationEnabled) {
				return formData;
			}

			const omittedFields: string[] = [];

			if (!editRouteCostCalculationAllowed) {
				omittedFields.push(ROUTE_COST_CALCULATION_TYPE_KEY, ROUTE_COST_CALCULATION_CONFIGURATION_KEY);
			}

			if (!editAdditionalCostsAllowed) {
				omittedFields.push(...ADDITIONAL_COSTS_KEYS);
			}

			return omit(formData, omittedFields);
		},
		[routeCostCalculationEnabled, editRouteCostCalculationAllowed, editAdditionalCostsAllowed]
	);

	const handleSubmit = useCallback(
		async (formData: Partial<UserTypeInterface>) => {
			try {
				const formValues = prepareFormData(formData);

				if (isEditMode && selectedUserType) {
					const userType = userTypeStore?.get(selectedUserType.id);
					await userType.update(formValues);

					Notification.success(
						t('USER_TYPE.USER_TYPE_UPDATED_SUCCESSFULLY', { userTypeTitle: formValues.title })
					);
				} else {
					const newUserType = new UserType(formValues, userTypeStore);
					await newUserType.create();
					Notification.success(
						t('USER_TYPE.USER_TYPE_CREATED_SUCCESSFULLY', { userTypeTitle: formValues.title })
					);
				}
				closeModal();
			} catch (error) {
				Notification.error(translations.failedToSaveUserType);
				throw error;
			}
		},
		[isEditMode, selectedUserType, userTypeStore, translations, closeModal, prepareFormData, t]
	);

	const modalConfig: ModalConfig = useMemo(() => {
		return {
			isOpen: Boolean(mode),
			isEditMode,
			translations,
			initialValues: selectedUserType,
			onSubmit: handleSubmit,
			onClose: closeModal
		};
	}, [mode, isEditMode, selectedUserType, translations, handleSubmit, closeModal]);

	return (
		<UserTypeModalContext.Provider
			value={{
				modalConfig,
				openCreateModal,
				openEditModal
			}}
		>
			{children}
		</UserTypeModalContext.Provider>
	);
};
